/**
 * Universal Importer Plugin (CSV Import)
 *
 * Provides universal CSV import functionality for Sponsors, Contacts, and other data types.
 * Supports intelligent source system detection (Salesforce, HubSpot, Pipedrive, etc.)
 * with automatic field mapping.
 *
 * Features:
 * - Multi-source system support (Salesforce, HubSpot, Pipedrive, Manual CSV, etc.)
 * - Intelligent auto-mapping based on source system
 * - Multi-file upload with relationship linking
 * - Field validation and error handling
 * - Preview and review before import
 */

import { logger } from '@/lib/platform/utils/logger';
import type { Plugin, PluginContext } from '@/lib/platform/types/plugins';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface UniversalImporterConfig {
  // No configuration needed for now - plugin is always available
}

export const universalImporterPlugin: Plugin<UniversalImporterConfig> = {
  manifest: {
    id: 'universal-importer',
    name: 'Universal Importer',
    version: '1.0.0',
    minSystemVersion: '1.0.0',
    pluginApiVersion: '1.0',
    description:
      'Import data from CSV files with intelligent auto-mapping. Supports Salesforce, HubSpot, Pipedrive, and custom CSV formats.',
    author: 'Valiyou',
    homepage: 'https://valiyou.com/plugins/universal-importer',
  },

  capabilities: {
    requiredEndpoints: [],
    requiredPermissions: ['write:sponsors', 'write:contacts'],

    features: {
      reports: false,
      dashboard: false,
      valuations: false,
      analytics: false,
      exports: false,
    },

    resourceLimits: {
      maxExecutionTimeMs: 30000, // 30 seconds for large imports
      maxApiCallsPerMinute: 60,
    },
  },

  lifecycle: {
    async onInstall() {
      logger.log('[Universal Importer] Plugin installed');
    },

    async onUninstall() {
      logger.log('[Universal Importer] Plugin uninstalled');
    },

    async onEnable() {
      logger.log('[Universal Importer] Plugin enabled');
    },

    async onDisable() {
      logger.log('[Universal Importer] Plugin disabled');
    },

    async healthCheck() {
      // Import plugin is always healthy - no external dependencies
      return {
        status: 'healthy',
        message: 'Ready to import data',
        lastCheck: new Date(),
      };
    },
  },

  enabled: false,
  currentConfig: undefined,
  context: undefined,

  settings: {
    component: null as any,
    schema: {
      type: 'object',
      properties: {},
      required: [],
    },
    defaultConfig: {},
    migrations: {},
  },

  async initialize(context: PluginContext) {
    universalImporterPlugin.context = context;
    universalImporterPlugin.currentConfig = {};
    context.logger.info('Universal Importer plugin initialized');
  },

  async cleanup() {
    if (universalImporterPlugin.context) {
      universalImporterPlugin.context.logger.info('Universal Importer plugin cleaning up');
    }
    universalImporterPlugin.context = undefined;
  },
};
